import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useStore } from '../store/store';

const CONTENTFUL_GRAPHQL_URL = import.meta.env.VITE_CONTENTFUL_GRAPHQL_URL;
const CONTENTFUL_ACCESS_TOKEN = import.meta.env.VITE_CONTENTFUL_ACCESS_TOKEN;

interface ContentfulResponse {
  data: {
    jsonContent: {
      sys: {
        id: string;
      };
      id: string;
      json: Record<string, string>;
    };
  };
}

interface UseGetLabelsParams {
  id: string;
  locale?: string;
  /** Override label language (e.g., 'en', 'de', 'fr' - forces specific language labels) */
  language?: string | null;
  enabled?: boolean;
}

/**
 * Map language code to Contentful locale
 * e.g., 'en' -> 'en-US', 'de' -> 'de-DE', 'fr' -> 'fr-FR'
 */
const getContentfulLocaleFromLanguage = (languageCode: string): string => {
  const languageToLocale: Record<string, string> = {
    en: 'en-US',
    de: 'de-DE',
    fr: 'fr-FR',
    nl: 'nl-NL',
    nb: 'nb-NO',
    ar: 'ar-SA',
  };
  return languageToLocale[languageCode.toLowerCase()] || 'en-US';
};

/**
 * Custom hook to fetch labels from Contentful using GraphQL API
 * Falls back to US English labels if the requested locale is not available
 *
 * @param id - The Contentful entry ID
 * @param locale - The locale to fetch labels for (default: 'en-US')
 * @param language - Override language code (e.g., 'en', 'de', 'fr')
 * @param enabled - Whether the query should run (default: true)
 *
 * @example
 * ```tsx
 * const { data: labels, isLoading, error } = useGetLabels({
 *   id: '60n6cJSqCElDfYLQN5Sczy',
 *   locale: 'de-DE',
 *   language: null, // Use native German labels
 * });
 *
 * // Force English labels even on German locale:
 * const { data: labels } = useGetLabels({
 *   id: '60n6cJSqCElDfYLQN5Sczy',
 *   locale: 'de-DE',
 *   language: 'en', // Force English labels
 * });
 * ```
 */
export const useGetLabels = ({
  id,
  locale = 'en-US',
  language = null,
  enabled = true,
}: UseGetLabelsParams) => {
  // If language is set, use the corresponding Contentful locale
  const effectiveLocale = language ? getContentfulLocaleFromLanguage(language) : locale;

  return useQuery({
    queryKey: ['contentful-labels', id, effectiveLocale],
    queryFn: async () => {
      const fetchLabels = async (targetLocale: string) => {
        const response = await axios.post<ContentfulResponse>(
          CONTENTFUL_GRAPHQL_URL,
          {
            query: `query ($id: String!, $locale: String!) { 
              jsonContent(id: $id, locale: $locale) { 
                sys { id } 
                id 
                json 
              } 
            }`,
            variables: {
              id,
              locale: targetLocale,
            },
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
            },
          }
        );

        return response.data.data.jsonContent;
      };

      try {
        const result = await fetchLabels(effectiveLocale);

        // If the result is null or doesn't have json, fallback to en-US
        if (!result || !result.json) {
          if (effectiveLocale !== 'en-US') {
            console.warn(
              `Labels not available for locale ${effectiveLocale}, falling back to en-US`
            );
            const fallbackResult = await fetchLabels('en-US');
            return fallbackResult?.json || {};
          }
          return {};
        }

        return result.json;
      } catch (error) {
        // If the requested locale fails, fallback to US English
        if (effectiveLocale !== 'en-US') {
          console.warn(
            `Failed to fetch labels for locale ${effectiveLocale}, falling back to en-US`
          );
          try {
            const fallbackResult = await fetchLabels('en-US');
            return fallbackResult?.json || {};
          } catch (fallbackError) {
            console.error('Failed to fetch fallback labels:', fallbackError);
            throw error;
          }
        }
        throw error;
      }
    },
    enabled,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: false, // Disable retry since we handle fallback internally
  });
};

/**
 * Simplified hook parameters for useLabels
 * Automatically uses language from store
 */
interface UseLabelsParams {
  id: string;
  locale?: string;
  enabled?: boolean;
}

/**
 * Convenience hook that wraps useGetLabels with automatic label language detection from store.
 * Pages should use this hook instead of useGetLabels for automatic language toggle support.
 *
 * @param id - The Contentful entry ID
 * @param locale - The locale to fetch labels for (default: 'en-US')
 * @param enabled - Whether the query should run (default: true)
 *
 * @example
 * ```tsx
 * const { data: labels, isLoading } = useLabels({
 *   id: '6dhtpdHCYn1jTZ1cRAXtJR',
 *   locale: convertToContentfulLocale(locale),
 * });
 * ```
 */
export const useLabels = ({ id, locale = 'en-US', enabled = true }: UseLabelsParams) => {
  const language = useStore((state) => state.language);

  return useGetLabels({
    id,
    locale,
    language,
    enabled,
  });
};
